
import loader from '../../partials/loader'
import scrollable from '../../partials/scrollable'

const cartRecommendations = (data) => ({
    ...loader,
    ...scrollable(data),
    url: data.url,
    isVertical: !data.vertical ? false : data.vertical,
    viewedFrom: !data.viewed_from ? 'Recently Viewed' : data.viewed_from,
    initialised: false,
    originalUrl: false,
    impressionPosition: 1,
    noOfRecords: Object.hasOwnProperty.call(data, 'noOfRecords') ? data.noOfRecords : 24,
    currentProducts: Object.hasOwnProperty.call(data, 'currentProducts') && Array.isArray(data.currentProducts) ? data.currentProducts.map((product) => parseInt(product.id_product, 10)) : [],
    pageData: [],
    title: '',
    // The basket pop-outs' lists name their pop-out's flag (showCart, showJustAdded) and load only while it's open:
    // the cart changes on every page, and nobody sees a closed pop-out's list. The latest list asked for meanwhile
    // waits for it to open.
    openWhen: data.openWhen || null,
    watchingOpen: false,
    pendingUrl: null,
    // The pop-outs' URLs end in ?product_ids=, so the ids go on the base path: after a second ? the server never
    // sees num_records and sends its default of 24
    cartUrl(productIds) {
        return `${this.url.split('?')[0]}?num_records=${this.noOfRecords}&product_ids=${productIds.join(',')}`
    },
    recommendWhenOpen(requestedUrl) {
        if (this.openWhen && !window.Alpine.evaluate(this.$el, this.openWhen)) {
            this.pendingUrl = requestedUrl
            return
        }

        this.pendingUrl = null
        this.startRecommending(requestedUrl)
    },
    cartUpdated(event) {
        this.startLoader()
        this.originalUrl = false
        this.pendingUrl = null
        this.currentProducts = event.detail.products.map((product) => parseInt(product.id_product, 10))

        // Only start recommending if cart has products
        if (this.currentProducts.length) {
            this.recommendWhenOpen(this.cartUrl(this.currentProducts))
        }
    },
    // The add-to-cart pop-out's list: what goes with the first product added
    productsAdded(event) {
        const added = Object.values(event.detail)[0]
        if (added) {
            this.recommendWhenOpen(this.cartUrl([added.id_product]))
        }
    },
    async init() {
        if (this.openWhen && !this.watchingOpen) {
            this.watchingOpen = true
            this.$watch(this.openWhen, (open) => {
                if (open && this.pendingUrl) {
                    this.recommendWhenOpen(this.pendingUrl)
                }
            })
        }

        if (!this.url || this.url.endsWith('?') || this.url.endsWith('=')) {
            return
        }

        this.initialised = false

        if (this.url.includes('?')) {
            this.startRecommending(`${this.url}&num_records=${this.noOfRecords}`)
        } else {
            this.startRecommending(this.originalUrl ? this.originalUrl : `${this.url}?num_records=${this.noOfRecords}&product_ids=${this.currentProducts.join(',')}`)
        }
    },
    async startRecommending(requestedUrl) {
        // we no longer paginate , clear data
        this.pageData = []
        this.initialised = false

        this.originalUrl = requestedUrl

        await window.api.get(requestedUrl).then((response) => {
            if (response.data.title) {
                this.title = response.data.title
                delete response.data.title
            }
            if (Object.keys(response.data).length) {
                this.pageData = Object.values(response.data)
            } else {
                this.pageData = []
            }
        }).catch(() => {
            this.pageData = []
        })

        for (let pos = 0; pos < this.pageData.length; pos += 1) {
            this.pageData[pos].ui_position = pos
        }

        // Without prices and stock there are no cards to draw: the strip closes, as when nothing comes back, rather
        // than pulsing for good
        if (this.pageData.length > 0) {
            const existingData = this.gatherExistingInformation(this.pageData)
            this.pageData = await this.getProductInformation(existingData.ids, existingData.data).catch(() => [])
        }

        this.initialised = true
        this.stopLoader()

        // The basket pop-out's vertical list has no scroller
        if (this.$refs.scrollable) {
            this.handleScroll()
        }
    },
    async getProductInformation(products, existingData) {
        const mergedProductData = []

        await window.api.getPricesAndStock(products).then((result) => {
            Object.values(result).forEach((product) => {
                mergedProductData.push({
                    ...product.data,
                    ...existingData[product.id],
                })
            })

            const impressionProducts = []
            const impressionProductsGA4 = []

            mergedProductData.forEach((product) => {
                impressionProducts.push({
                    name: product.name,
                    id: product.reference,
                    price: product.price,
                    brand: product.manufacturer_name,
                    category: product.category_name,
                    list: this.viewedFrom,
                    position: this.impressionPosition,
                })

                impressionProductsGA4.push({
                    item_name: product.name,
                    item_id: product.reference,
                    price: product.price,
                    brand: product.manufacturer_name,
                    category: product.category_name,
                    item_list_name: this.viewedFrom,
                    item_list_id: this.viewedFrom.replace(/[^0-9a-z]/gi, '_').toLowerCase(),
                    index: this.impressionPosition - 1,
                })

                this.impressionPosition += 1
            })

            window.dispatchEvent(new CustomEvent('gtmdata', {
                detail: {
                    ecommerce: {
                        currencyCode: window.Alpine.store('currency').iso_code,
                        impressions: impressionProducts,
                    },
                    event: 'impression',
                },
            }))

            window.dispatchEvent(new CustomEvent('gtmdata', {
                detail: {
                    event: 'view_item_list',
                    ecommerce: {
                        item_list_id: this.viewedFrom.replace(/[^0-9a-z]/gi, '_').toLowerCase(),
                        item_list_name: this.viewedFrom,
                        currency: window.Alpine.store('currency').iso_code,
                        currencyCode: window.Alpine.store('currency').iso_code,
                        items: impressionProductsGA4,
                    },
                },
            }))
        })

        mergedProductData.sort((a, b) => ((a.ui_position > b.ui_position) ? 1 : ((b.ui_position > a.ui_position) ? -1 : 0)))
        return mergedProductData
    },
    gatherExistingInformation(dataSet) {
        const productsToGet = []
        const dataToMerge = {}

        dataSet.forEach((product) => {
            productsToGet.push({
                id: product.id_product, quantity: 1, index: 0,
            })

            dataToMerge[product.id_product] = product
        })

        return { ids: productsToGet, data: dataToMerge }
    },
    async correctPreloadedData() {
        const existingData = this.gatherExistingInformation(this.preloaded)
        this.preloaded = await this.getProductInformation(existingData.ids, existingData.data)
    },
})
document.addEventListener('alpine:init', () => {
    window.Alpine.data('cartRecommendations', cartRecommendations)
})
